import Link from "next/link";
import Image from "next/image";

import clsx from "clsx";
import Price from "./Price";

import { Product } from "@/lib/shopify/types";
import placeholder from "@/public/shop/placeholder.svg";

export default function ProductItem({
  item,
  showLabel = true,
  isInteractive = true,
  active = false,
  size = "lg",
  priority,
}: {
  item: Product;
  showLabel: boolean;
  isInteractive: boolean;
  active?: boolean;
  size?: "sm" | "md" | "lg";
  priority?: boolean;
}) {
  if (!item) {
    return null;
  }

  return (
    <Link
      className="relative block w-full"
      href={`/shop/product/${item.handle}`}
      prefetch={true}
    >
      <div
        className={clsx(
          "group flex flex-col w-full items-center justify-center",
          size === "sm" && "!h-[60rem] lg:!h-[60rem]",
          size === "md" && "!h-[60rem] lg:!h-[65rem]",
          size === "lg" && "!h-[60rem] lg:!h-[68.75rem]",
          {
            relative: showLabel,
            "border-2 border-[#D9F9B9]": active,
            "border-neutral-200 dark:border-neutral-800": !active,
          },
        )}
      >
        {/* tags */}
        <div className="absolute z-[1] top-[1.5rem] left-[1.5rem] flex gap-[1rem]">
          {/* new */}
          {item?.tags?.includes("new") && (
            <div className="bg-white text-black px-[1rem] h-[4rem] min-h-[4rem] max-h-[4rem] font-geist text-[2rem] border border-black/10 leading-[1em] flex items-center justify-center">
              Neu
            </div>
          )}
          {/* discount */}
          {item?.tags?.find((tag) => tag.startsWith("off")) && (
            <div className="bg-[#D93D4F] text-white px-[1rem] h-[4rem] min-h-[4rem] max-h-[4rem] border border-[#D93D4F] font-geist text-[2rem] leading-[1em] flex items-center justify-center">
              {item?.tags
                ?.find((tag) => tag.startsWith("off"))
                ?.replace("off", "")}
              % RABATT
            </div>
          )}
          {/*  */}
        </div>
        {/* end tags */}

        <div className="w-full h-full overflow-clip border group-hover:border-[#D9F9B9] mb-[1.75rem] bg-[#DEDCDA]">
          <Image
            src={item?.featuredImage?.url ?? placeholder}
            alt={item.title}
            priority={priority}
            fill
            className={clsx("!relative h-full w-full object-cover mix-blend-darken", {
              "transition duration-300 ease-in-out group-hover:scale-105":
                isInteractive,
            })}
          />
        </div>

        {showLabel ? (
          <div className="flex h-fit w-full @container/label">
            <div className="w-full flex justify-between">
              <div>
                <h4 className="line-clamp-2 grow text-[2.25rem] text-black font-geist">
                  {item.title}
                </h4>
                <Price
                  className="flex-none text-black/50"
                  amount={item.priceRange.maxVariantPrice.amount}
                  currencyCode={item.priceRange.maxVariantPrice.currencyCode}
                  currencyCodeClassName="hidden @[275px]/label:inline"
                />
              </div>

              <div className="flex gap-[.5rem] mt-1">
                {item?.options
                  ?.find((option) => option.name === "Color")
                  ?.values?.map((colorValue, colorIndex) => (
                    <span
                      key={colorIndex}
                      className="w-[2rem] h-[2rem] min-w-[2rem] min-h-[2rem]"
                      style={{ background: colorValue }}
                    ></span>
                  ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Link>
  );
}
